import { ApiError } from "@/shared/api/http-client";

/**
 * Backend communications-admin error envelope. Every 4xx from the use cases
 * in `wanderfit-api/src/communications/engine/admin/` carries a structured
 *
 *     { errorCode: string, message: string }
 *
 * (see `communication-admin.errors.ts` + the config/locale guards added for
 * API-COMM-1…3). Unlike TOS there is no `details` payload today.
 */
interface CommunicationErrorBody {
  readonly errorCode?: string;
  readonly message?: string;
}

/**
 * Closed set of communications error codes the BO renders with curated
 * copy. Mirrors `COMMUNICATION_ADMIN_ERRORS` plus the guard codes defined
 * for the config / locale / translation-create endpoints (API-COMM-1…3).
 * Every value MUST have a matching `communications.errors.<key>` in both
 * `en/common.json` and `es/common.json` — the parity test guards that.
 *
 * Codes NOT listed here fall through to `resolveMutationErrorMessage`'s
 * server-message ladder (the same graceful path compliance uses) — so a new
 * backend guard never surfaces as a raw `Server: …` without us noticing in
 * review, it just shows the server's own message.
 */
// Object keys MUST match the API's `COMMUNICATION_ADMIN_ERRORS` values exactly
// (wanderfit-api/src/communications/engine/admin/communication-admin.errors.ts)
// — the lookup keys off the raw `errorCode` string.
export const COMMUNICATION_ERROR_CODE_KEYS = {
  TRANSLATION_NOT_FOUND: "communications.errors.translationNotFound",
  NO_FIELDS_PROVIDED: "communications.errors.noFieldsProvided",
  SUBJECT_REQUIRED: "communications.errors.subjectRequired",
  // Locales (A)
  LOCALE_ALREADY_EXISTS: "communications.errors.localeAlreadyExists",
  LOCALE_NOT_FOUND: "communications.errors.localeNotFound",
  // Create-first-translation (B) + channel add/delete (BO-4.2)
  CHANNEL_NOT_FOUND: "communications.errors.channelNotFound",
  CHANNEL_ALREADY_EXISTS: "communications.errors.channelAlreadyExists",
  TRANSLATION_ALREADY_EXISTS: "communications.errors.translationAlreadyExists",
  // Config + channel toggle (C)
  COMMUNICATION_NOT_FOUND: "communications.errors.communicationNotFound",
  EMAIL_CHANNEL_REQUIRED: "communications.errors.emailChannelRequired",
  NO_ACTIVE_CHANNELS: "communications.errors.cannotLeaveNoActiveChannels",
  CANNOT_DISABLE_EMAIL_FLOOR: "communications.errors.cannotDisableEmailFloor",
  CANNOT_ENABLE_NON_EMAIL_ON_CRITICAL: "communications.errors.criticalRequiresEmailOnly",
} as const;

export type KnownCommunicationErrorCode = keyof typeof COMMUNICATION_ERROR_CODE_KEYS;

export interface ResolvedCommunicationError {
  /** The i18n key the UI should render. */
  readonly i18nKey: string;
  /** Raw backend error code for logging / testid suffixes. */
  readonly code: string;
}

function isCommunicationErrorBody(value: unknown): value is CommunicationErrorBody {
  return typeof value === "object" && value !== null && "errorCode" in value;
}

/**
 * Best-effort lookup. Returns `null` when the error doesn't carry one of the
 * known codes — callers fall through to the shared generic resolver.
 */
export function resolveCommunicationErrorKey(err: unknown): ResolvedCommunicationError | null {
  if (!(err instanceof ApiError)) return null;
  if (!isCommunicationErrorBody(err.body)) return null;
  const code = err.body.errorCode;
  if (typeof code !== "string") return null;
  const key = (COMMUNICATION_ERROR_CODE_KEYS as Record<string, string>)[code];
  if (!key) return null;
  return { i18nKey: key, code };
}
